import React, { useState } from "react";
import {
  ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, ReferenceLine,
} from "recharts";

export const SYMBOL_COLORS = {
  NAS100: "#D6FF38", XAUUSD: "#FBBF24", US30: "#22D3EE", EURUSD: "#A78BFA", GBPUSD: "#F472B6",
  USDJPY: "#FB923C", AUDUSD: "#34D399", USDCAD: "#F87171", NZDUSD: "#60A5FA", EURJPY: "#C084FC",
  GBPJPY: "#E879F9", EURGBP: "#2DD4BF", XAGUSD: "#CBD5E1", US500: "#A3E635", GER40: "#FACC15",
  UK100: "#38BDF8", JPN225: "#FDA4AF",
};
const colorOf = (s) => SYMBOL_COLORS[s] || "#94A3B8";
const gbp = (v, d = 0) => `£${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d })}`;
const tip = { background: "#050914", border: "1px solid #1F2937", fontSize: 11 };
const axis = { fill: "#94A3B8", fontSize: 10 };

const Card = ({ title, testid, children, right }) => (
  <div data-testid={testid} className="mt-6 border border-[#1F2937] rounded-lg p-4 bg-[#0B0D14]">
    <div className="flex items-center justify-between mb-3">
      <div className="mono text-[10px] uppercase tracking-widest text-[#64748B]">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

export function BasketEquityChart({ result }) {
  const [showBots, setShowBots] = useState(true);
  const syms = result.correlation_symbols || [];
  const data = [{ month: "start", equity: result.stats.start_balance,
    ...Object.fromEntries(syms.map((s) => [s, 0])) },
  ...result.monthly.map((m) => ({ month: m.month, equity: m.equity, ...m.bot_contrib }))];
  const toggle = (
    <button data-testid="basket-toggle-bot-lines" onClick={() => setShowBots((v) => !v)}
      className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-[#1F2937] text-[#94A3B8] hover:text-[#F1F5F9] transition-colors">
      {showBots ? "Hide" : "Show"} per-bot £ contributed
    </button>
  );
  return (
    <Card testid="basket-equity-chart" title="Shared account equity / balance (month-end) · thin lines = cumulative £ each bot added" right={toggle}>
      <ResponsiveContainer width="100%" height={300} initialDimension={{ width: 800, height: 300 }}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 10 }}>
          <defs>
            <linearGradient id="eqFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2ECA7F" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#2ECA7F" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
          <XAxis dataKey="month" tick={axis} interval={2} />
          <YAxis tick={axis} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
          <Tooltip contentStyle={tip} formatter={(v, n) => [gbp(v), n === "equity" ? "Basket" : n]} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <Area type="monotone" dataKey="equity" name="equity" stroke="#2ECA7F" strokeWidth={2.5} fill="url(#eqFill)" />
          {showBots && syms.map((s) => (
            <Line key={s} type="monotone" dataKey={s} stroke={colorOf(s)} strokeWidth={1.2} dot={false} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
}

export function BasketMonthlyChart({ result }) {
  const syms = result.correlation_symbols || [];
  const target = result.target_monthly_gbp;
  const data = result.monthly.map((m) => ({ month: m.month, total: m.total_pnl, ...m.pnl }));
  return (
    <Card testid="basket-monthly-chart" title="Monthly P&L - stacked by symbol, line = basket total">
      <ResponsiveContainer width="100%" height={260} initialDimension={{ width: 800, height: 260 }}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 10 }} stackOffset="sign">
          <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" />
          <XAxis dataKey="month" tick={axis} interval={2} />
          <YAxis tick={axis} tickFormatter={(v) => `£${(v / 1000).toFixed(1)}k`} />
          <Tooltip contentStyle={tip} formatter={(v, n) => [gbp(v), n]} />
          <Legend wrapperStyle={{ fontSize: 10 }} />
          <ReferenceLine y={0} stroke="#334155" />
          {target > 0 && <ReferenceLine y={target} stroke="#FBBF24" strokeDasharray="4 4" label={{ value: "target", fill: "#FBBF24", fontSize: 9 }} />}
          {syms.map((s) => <Bar key={s} dataKey={s} stackId="pnl" fill={colorOf(s)} />)}
          <Line type="monotone" dataKey="total" name="basket total" stroke="#F1F5F9" strokeWidth={2} dot={{ r: 2 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
}

export function BasketBotsTable({ basket }) {
  const cols = ["Sym", "Bot", "TF", "Mo", "Net £", "£/mo", "Trades", "Win %", "PF", "DD %", "Sharpe", "+/- months", "Robustness", "2025+ test"];
  return (
    <Card testid="basket-table-card" title={`Selected basket · best bot per symbol · ${basket.length} bots`}>
      <div className="overflow-x-auto">
        <table data-testid="basket-table" className="w-full mono text-[11px] whitespace-nowrap">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#64748B] border-b border-[#1F2937]">
              {cols.map((c, i) => <th key={c} className={`py-2 px-2 ${i < 3 ? "text-left" : "text-right"}`}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {basket.map((b, i) => (
              <tr key={b.bot_name} data-testid={`basket-row-${i}`} className="border-b border-[#111827] hover:bg-[#111827] transition-colors">
                <td className="py-2 px-2 font-medium" style={{ color: colorOf(b.symbol) }}>{b.symbol}</td>
                <td className="py-2 px-2 text-[#F1F5F9]">
                  {b.bot_name}
                  {!b.monthly_complete && (
                    <span data-testid={`basket-row-${i}-incomplete`} className="ml-2 text-[9px] text-[#FBBF24]"
                      title={`Monthly rows add up to ${gbp(b.monthly_sum)} vs ${gbp(b.net_profit)} reported - older report parser (pre v1.21). Re-test for exact monthly figures.`}>
                      ⚠ monthly incomplete
                    </span>
                  )}
                </td>
                <td className="py-2 px-2 text-[#94A3B8]">{b.period}</td>
                <td className="py-2 px-2 text-right" style={{ color: b.months >= 24 ? "#64748B" : "#FBBF24" }}>{b.months}</td>
                <td className="py-2 px-2 text-right text-[#2ECA7F]">{gbp(b.net_profit)}</td>
                <td className="py-2 px-2 text-right text-[#2ECA7F]">{gbp(b.mean_monthly)}</td>
                <td className="py-2 px-2 text-right" style={{ color: b.total_trades >= 1000 ? "#2ECA7F" : "#F1F5F9" }}>{b.total_trades}</td>
                <td className="py-2 px-2 text-right text-[#94A3B8]">{b.win_rate.toFixed(1)}</td>
                <td className="py-2 px-2 text-right" style={{ color: b.profit_factor >= 1.3 ? "#D6FF38" : "#94A3B8" }}>{b.profit_factor.toFixed(2)}</td>
                <td className="py-2 px-2 text-right text-[#94A3B8]">{b.max_dd_pct.toFixed(1)}</td>
                <td className="py-2 px-2 text-right text-[#94A3B8]">{b.sharpe_ratio.toFixed(2)}</td>
                <td className="py-2 px-2 text-right"><span className="text-[#2ECA7F]">{b.profit_months}</span>/<span className="text-[#F87171]">{b.losing_months}</span></td>
                <td data-testid={`basket-row-${i}-tier`} className="py-2 px-2 text-right"
                  style={{ color: b.robust_tier === "proven" ? "#2ECA7F" : b.robust_tier === "unvalidated" ? "#64748B" : "#22D3EE" }}>
                  {b.robust_tier || "-"}
                </td>
                <td data-testid={`basket-row-${i}-oos`} className="py-2 px-2 text-right">
                  {!b.oos ? <span className="text-[#64748B]">not tested</span>
                    : <span style={{ color: b.validated ? "#2ECA7F" : "#F87171" }}>
                        {b.validated ? "✓ " : "✗ "}{gbp(b.oos.net_profit)} · PF {Number(b.oos.profit_factor || 0).toFixed(2)}
                      </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function BasketMonthlyTable({ result }) {
  const syms = result.correlation_symbols || [];
  const cell = (v) => <span style={{ color: v >= 0 ? "#2ECA7F" : "#F87171" }}>{gbp(v)}</span>;
  return (
    <Card testid="basket-monthly-table-card" title={`Combined monthly breakdown · ${result.monthly.length} months`}>
      <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
        <table data-testid="basket-monthly-table" className="w-full mono text-[11px] whitespace-nowrap">
          <thead className="sticky top-0 bg-[#0B0D14]">
            <tr className="text-[10px] uppercase tracking-widest text-[#64748B] border-b border-[#1F2937]">
              <th className="text-left py-2 px-2">Month</th>
              {syms.map((s) => <th key={s} className="text-right py-2 px-2" style={{ color: colorOf(s) }}>{s}</th>)}
              <th className="text-right py-2 px-2">Basket P&L</th>
              <th className="text-right py-2 px-2">Trades</th>
              <th className="text-right py-2 px-2">W / L</th>
              <th className="text-right py-2 px-2">Equity</th>
            </tr>
          </thead>
          <tbody>
            {result.monthly.map((m) => (
              <tr key={m.month} data-testid={`basket-month-${m.month}`} className="border-b border-[#111827] hover:bg-[#111827] transition-colors">
                <td className="py-1.5 px-2 text-[#F1F5F9]">{m.month}</td>
                {syms.map((s) => <td key={s} className="py-1.5 px-2 text-right">{cell(m.pnl[s])}</td>)}
                <td className="py-1.5 px-2 text-right font-medium">{cell(m.total_pnl)}</td>
                <td className="py-1.5 px-2 text-right text-[#F1F5F9]">{m.trades}</td>
                <td className="py-1.5 px-2 text-right"><span className="text-[#2ECA7F]">{m.wins}</span>/<span className="text-[#F87171]">{m.losses}</span></td>
                <td className="py-1.5 px-2 text-right text-[#22D3EE]">{gbp(m.equity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function BasketCorrelation({ result }) {
  const syms = result.correlation_symbols || [];
  const bg = (v) => (v >= 0 ? `rgba(248,113,113,${Math.abs(v) * 0.6})` : `rgba(46,202,127,${Math.abs(v) * 0.6})`);
  return (
    <Card testid="basket-correlation" title={`Monthly P&L correlation · avg |r| ${(result.avg_pair_correlation || 0).toFixed(2)} (lower = better diversification)`}>
      <div className="overflow-x-auto">
        <table className="mono text-[11px]">
          <thead>
            <tr><th />{syms.map((s) => <th key={s} className="px-3 py-1" style={{ color: colorOf(s) }}>{s}</th>)}</tr>
          </thead>
          <tbody>
            {syms.map((a, i) => (
              <tr key={a}>
                <td className="px-3 py-1 font-medium" style={{ color: colorOf(a) }}>{a}</td>
                {result.correlation[i].map((v, j) => (
                  <td key={j} data-testid={`corr-${a}-${syms[j]}`} className="px-3 py-1.5 text-center text-[#F1F5F9]"
                    style={{ background: i === j ? "#1F2937" : bg(v) }}>{v.toFixed(2)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function CompoundToggle({ mode, setMode }) {
  const opt = (m, label) => (
    <button data-testid={`compound-toggle-${m}`} onClick={() => setMode(m)}
      className={`mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded transition-colors ${mode === m ? "bg-[#2ECA7F] text-[#050914]" : "text-[#94A3B8] hover:text-[#F1F5F9]"}`}>
      {label}
    </button>
  );
  return (
    <div className="mt-8 flex items-center gap-3">
      <div data-testid="compound-toggle" className="inline-flex border border-[#1F2937] rounded p-0.5 bg-[#050914]">
        {opt("fixed", "Compounding off")}{opt("compound", "Compounding on")}
      </div>
      <span className="mono text-[10px] text-[#64748B]">
        {mode === "fixed" ? "Fixed lot sizes from the start balance - the realistic baseline" : "Lots grow with the account - best-case upside"}
      </span>
    </div>
  );
}

export function BasketProjection({ projection }) {
  if (!projection) return null;
  const p = projection;
  const cells = [6, 12, 24];
  return (
    <Card testid="basket-projection" title={`Forward projection · ${p.monthly_return_pct}%/month combined estimate`}>
      <div className="mono text-[10px] text-[#64748B] mb-3">
        Uses each bot&apos;s 2025+ out-of-sample return when tested, otherwise HALF its backtest return (curve-fit haircut).
        Not a guarantee - a planning range.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <table className="mono text-[11px] w-full">
          <thead><tr className="text-[10px] uppercase tracking-widest text-[#64748B]"><th className="text-left py-1">Months</th>
            <th className="text-right py-1">Fixed lots</th><th className="text-right py-1">Compounded</th></tr></thead>
          <tbody>
            {cells.map((n) => (
              <tr key={n} data-testid={`projection-${n}`} className="border-t border-[#111827]">
                <td className="py-1.5 text-[#F1F5F9]">{n}</td>
                <td className="py-1.5 text-right text-[#2ECA7F]">{gbp(p[`fixed_${n}`])}</td>
                <td className="py-1.5 text-right text-[#22D3EE]">{gbp(p[`compound_${n}`])}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <table className="mono text-[11px] w-full">
          <tbody>
            {p.bots.map((b) => (
              <tr key={b.bot_name} className="border-t border-[#111827]">
                <td className="py-1.5" style={{ color: colorOf(b.symbol) }}>{b.symbol}</td>
                <td className="py-1.5 text-right" style={{ color: b.monthly_return_pct >= 0 ? "#2ECA7F" : "#F87171" }}>{b.monthly_return_pct}%/mo</td>
                <td className="py-1.5 pl-3 text-[#64748B]">{b.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
